import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CompanyTags } from "@/components/CompanyTags";
import { Button, ErrorState, Spinner, StatusBadge, Tag } from "@/components/ui";
import type { CompanyMatch, ReceptionCompany } from "./queries";
import { useCompanyFile } from "./queries";
import type { Capture } from "./rows";
import { AssignButton, OwnerTag, PersonLine } from "./rows";

/** A company row with or without the columns only a name search brings. */
type Row = ReceptionCompany &
  Partial<
    Pick<
      CompanyMatch,
      | "city"
      | "contact_count"
      | "matched_keywords"
      | "recording_notice_given"
      | "recording_refused"
    >
  >;

/**
 * Everybody at the company, each with the button that hands them the call.
 *
 * Loaded when the row is opened, not with the search: a search returns up to
 * fifty companies and reception opens one of them.
 */
function CompanyContacts({ company, capture }: { company: Row; capture: Capture }) {
  const { t } = useTranslation();
  const file = useCompanyFile(company.id);

  if (file.isPending) return <Spinner label={t("reception.loadingContacts")} />;
  if (file.isError) {
    return (
      <ErrorState
        message={
          file.error instanceof Error
            ? file.error.message
            : t("errors.receptionCompany")
        }
        onRetry={() => void file.refetch()}
      />
    );
  }
  if (file.data.contacts.length === 0) {
    return <p className="py-3 text-sm text-mute-600">{t("reception.noContacts")}</p>;
  }
  return (
    <ul>
      {file.data.contacts.map((contact) => {
        const disabled = contact.disabled_at !== null;
        return (
          <li
            key={contact.id}
            className="flex flex-wrap items-center justify-between gap-3 border-t border-rule py-2.5"
          >
            <PersonLine
              name={contact.name}
              jobTitle={contact.job_title ?? ""}
              phones={contact.phones.map((row) => row.phone)}
              emails={contact.emails.map((row) => row.email)}
              disabled={disabled}
            />
            {disabled ? null : (
              <AssignButton capture={capture} company={company} contact={contact} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One company the search found, and the way from it to a contact.
 *
 * The same row wherever the company turns up — under its name, under a keyword,
 * or because the number called from is a branch's sales line — so opening it in
 * one section opens it in all of them (`capture.expanded` is keyed by company).
 */
export function CompanyRow({
  company,
  capture,
  note,
}: {
  company: Row;
  capture: Capture;
  /** Why the row is here, where its name alone does not say. */
  note?: ReactNode;
}) {
  const { t } = useTranslation();
  const open = capture.expanded.has(company.id);
  const label = company.name || company.legal_name;

  return (
    <li className="border-2 border-rule bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-display text-[15px] font-extrabold">{label}</span>
            <StatusBadge status={company.status} label={company.status_display} />
            <CompanyTags company={company} />
            <OwnerTag company={company} />
          </p>
          <p className="mt-0.5 text-xs text-mute-600">
            {[company.legal_name !== label ? company.legal_name : null, company.city]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {note ? <p className="mt-1 text-xs text-brand-700">{note}</p> : null}
          {company.matched_keywords && company.matched_keywords.length > 0 ? (
            <p className="mt-1.5 flex flex-wrap gap-1.5">
              {company.matched_keywords.map((keyword) => (
                <Tag key={keyword} tone="outline">
                  {keyword}
                </Tag>
              ))}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            className="px-3 py-1.5 text-sm"
            aria-expanded={open}
            onClick={() => capture.toggle(company.id)}
          >
            {open
              ? t("reception.hideContacts")
              : company.contact_count === undefined
                ? t("reception.showContacts")
                : t("reception.showContactsCount", { count: company.contact_count })}
          </Button>
          <Button
            className="px-3 py-1.5 text-sm"
            disabled={capture.busy}
            onClick={() => capture.addContact(company)}
          >
            {t("reception.newContact")}
          </Button>
        </div>
      </div>

      {open ? (
        <div className="mt-3">
          <CompanyContacts company={company} capture={capture} />
        </div>
      ) : null}
    </li>
  );
}
